import { useColorMode } from '@chakra-ui/react';
import { Button, RoundBadge } from '@peacecover/component';
import { ArrowRightSmall, PeopleFill } from '@peacecover/icon';
import { t } from 'i18next';
import { isEmpty } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Virtuoso } from 'react-virtuoso';
import useChatRoomsQuery from '../../components/api/queries/useChatRoomsQuery';
import useRefreshJwtQuery from '../../components/api/queries/useRefreshJwtQuery';
import useUserQuery from '../../components/api/queries/useUserQuery';
import { FEATURE_KEYS } from '../../domains/features/constants';
import FeatureGate from '../../domains/features/ui/components/FeatureGate';
import { ReactComponent as PlusProfile } from '../../icons/plus.profile.svg';
import HomeBottomNav from '../../layout/HomeBottomNav';
import { getLanguage } from '../../utils/etcUtils';
import { openNewTab } from '../../utils/UIUtils';
import saveData from '../../utils/saveData';
import useArtistQueryLikedOnly from '../hooks/queries/useArtistQueryLikedOnly';
import HorizontalScroll from '../my-page/components/HorizontalScroll';
import NotificationBadge from '../my-page/components/NotificationBadge';
import NavigateArtistSelectButton from '../select-artist/NavigateArtistSelectButton';
import ChatItem from './components/ChatItem';
import ChatItemListSkeleton from './components/ChatItemListSkeleton';
import { useAdPopcorn } from './hooks/useAdPopcorn';
import { usePushNotification } from './hooks/usePushNotification';
import type { ChatRoomType, InfiniteChatRoomsData } from './types/ChatMessageType';
import BettieSatisfactionBottomDrawer from '../../components/drawer/BettieSatisfactionBottomDrawer';
import FollowersProfileSection from './components/FollowersProfileSection';
import EmptyCase from '../../components/errors/EmptyCase';
import BettieLetterEntryCard from './components/BettieLetterEntryCard';

const ALL_MESSAGE_CATEGORIES = ['All', 'DM', 'Selling', 'Purchasing', 'Exchanging'];
const BETTIE_IOS_STORE_URL = 'https://apps.apple.com/app/bettie/id6769073576';
const BETTIE_ANDROID_STORE_URL =
  'https://play.google.com/store/apps/details?id=com.peacecover.bettie';

const getBettieDownloadUrl = () => {
  if (typeof navigator === 'undefined') {
    return BETTIE_IOS_STORE_URL;
  }

  const userAgent = navigator.userAgent || '';
  const isIPadOS = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;

  if (/android/i.test(userAgent)) {
    return BETTIE_ANDROID_STORE_URL;
  }

  if (/iPad|iPhone|iPod/i.test(userAgent) || isIPadOS) {
    return BETTIE_IOS_STORE_URL;
  }

  return BETTIE_IOS_STORE_URL;
};

const ChatMainPage = () => {
  const location = useLocation();
  const { i18n } = useTranslation();

  const [chatRoomsCategory, setChatRoomsCategory] = useState('All');
  const [paginatedChatRooms, setPaginatedChatRooms] = useState<ChatRoomType[]>();
  const [isRequestArtistSelectPopupOpen, setIsRequestArtistSelectPopupOpen] = useState(false);
  const [isBettiePromotionPopupOpen, setIsBettiePromotionPopupOpen] = useState(
    localStorage.getItem('isBettiePromotionPopupClosed') !== 'true'
  );
  const [isBettieSatisfactionPopupOpen, setIsBettieSatisfactionPopupOpen] = useState(
    location.state?.title === 'bettieSatisfaction'
  );
  const [isPocapanNoticePopupOpen, setIsPocapanNoticePopupOpen] = useState(true);
  const { colorMode } = useColorMode();

  const { data, isFetching, fetchNextPage, hasNextPage } = useChatRoomsQuery({
    type: chatRoomsCategory,
    tradingPhotoCardId: undefined,
    onlyJoined: true
  });

  const { data: likedArtists, isLoading: isLoadingLikedArtists } = useArtistQueryLikedOnly();
  usePushNotification(undefined, 'chatMain');

  const navigate = useNavigate();
  const user = useUserQuery();

  useEffect(() => {
    if (isLoadingLikedArtists) return;
    if (likedArtists && likedArtists.length === 0) {
      setIsRequestArtistSelectPopupOpen(true);
    } else {
      setIsRequestArtistSelectPopupOpen(false);
    }
  }, [likedArtists, isLoadingLikedArtists]);

  const authToken = useRefreshJwtQuery();
  if (authToken?.jwt) {
    saveData('token', JSON.stringify(authToken?.jwt));
  }

  useEffect(() => {
    if (user) {
      saveData('userId', user?.userId);
      saveData('email', user?.email);
    }
    if (user && !isEmpty(user.locale) && getLanguage() !== user.locale) {
      saveData('i18nextLng', user.locale);
      i18n.changeLanguage(user.locale);
    }
  }, [user, i18n]);

  useEffect(() => {
    localStorage.removeItem('latestPostId');
  }, []);

  const handlePaginatedChatRooms = useCallback((paginatedData: InfiniteChatRoomsData) => {
    const chatRooms = paginatedData.pages?.flatMap((page) => page.chatRooms) ?? [];
    const sorted = [...chatRooms].sort((a, b) => {
      const aTime = a?.lastMessage?.sentAt ? new Date(a.lastMessage.sentAt).getTime() : 0;
      const bTime = b?.lastMessage?.sentAt ? new Date(b.lastMessage.sentAt).getTime() : 0;
      return bTime - aTime;
    });
    setPaginatedChatRooms(sorted);
  }, []);

  const loadMore = () => {
    if (!isFetching && hasNextPage) {
      fetchNextPage();
    }
  };

  const handleNavigateArtistSelectButton = () => {
    if (user.baseUserProfile?.nickname) {
      navigate('/search-artists', { replace: true });
    } else {
      navigate('/nickname-setting?init=true', { replace: true });
    }
    setIsRequestArtistSelectPopupOpen(false);
  };

  useEffect(() => {
    if (!isEmpty(data)) {
      handlePaginatedChatRooms(data);
    }
  }, [data, handlePaginatedChatRooms]);

  const existingBettie = paginatedChatRooms?.filter(
    (chatRoom) => chatRoom.subjectType === 'MemberPersona'
  )[0];

  const isLikeSpecificArtist = (artistId: string) => {
    if (!likedArtists) return false;
    return likedArtists.some((artist) => artist.artistId === artistId);
  };

  return (
    <div className="min-h-screen pt-[calc(env(safe-area-inset-top))] pb-[calc(120px+env(safe-area-inset-bottom))] bg-background">
      <Helmet>
        <title>{t('tabs.chat')}</title>
        <meta name="theme-color" content={colorMode === 'dark' ? '#0D0E12' : '#FFFFFF'} />
      </Helmet>
      <header className="flex items-center justify-between px-[24px] py-[11px]">
        <h1 className="text-[26px] font-bold text-text-main-lv1">{t('ChatPage.title')}</h1>
        <NotificationBadge />
      </header>
      <BettieAppDownloadBanner />
      <FollowersProfileSection userProfileId={user?.baseUserProfile?.userProfileId} />
      <FeatureGate featureKey={FEATURE_KEYS.CARD_REVIEW} inverted>
        <HorizontalScroll>
          <div className="flex gap-[8px] px-[20px] py-[8px] mb-[8px]">
            {ALL_MESSAGE_CATEGORIES.map((category) => (
              <button
                key={category}
                type="button"
                onClick={() => {
                  setPaginatedChatRooms(undefined);
                  setChatRoomsCategory(category);
                }}
                className={`rounded-[32px] px-[12px] py-[6px] text-[13px] font-semibold leading-none border transition-colors
                ${
                  category === chatRoomsCategory
                    ? 'bg-text-main-lv2 text-pure-white dark:text-pure-black border-transparent'
                    : 'bg-on-primary dark:bg-transparent text-text-main-lv2 border-separator2'
                }
              `}
              >
                {t(`notifications.messageCategory${category}`)}
              </button>
            ))}
          </div>
        </HorizontalScroll>
      </FeatureGate>

      {/* 메시지 목록 */}
      <div className="pb-[24px]">
        {paginatedChatRooms === undefined ? (
          <ChatItemListSkeleton />
        ) : (
          <>
            <FeatureGate featureKey={FEATURE_KEYS.CARD_REVIEW} inverted>
              {chatRoomsCategory === 'All' && (
                <>
                  <ChatItem variant="lounge" categoryGroup="community" />
                  {isLikeSpecificArtist('nctwish') && (
                    <ChatItem variant="lounge" categoryGroup="nctwish" />
                  )}
                  {!existingBettie && (
                    <FeatureGate featureKey={FEATURE_KEYS.BETTIE_BETA_USER}>
                      <BettieEntryCard />
                    </FeatureGate>
                  )}
                  <FeatureGate featureKey={FEATURE_KEYS.BETTIE_LETTER}>
                    <BettieLetterEntryCard />
                  </FeatureGate>
                  <FeatureGate featureKey={FEATURE_KEYS.AD_POPCORN}>
                    <FreeChargeEntryCard />
                  </FeatureGate>
                </>
              )}
            </FeatureGate>
            {paginatedChatRooms.length > 0 ? (
              <Virtuoso
                useWindowScroll
                overscan={480}
                defaultItemHeight={72}
                style={{ minHeight: 100 }}
                data={paginatedChatRooms ?? []}
                endReached={loadMore}
                itemContent={(_index, chatRoom: ChatRoomType) => (
                  <ChatItem variant="chat" chatRoom={chatRoom} />
                )}
              />
            ) : chatRoomsCategory === 'All' ? null : (
              // @ts-ignore
              <EmptyCase description={t('ChatPage.emptyChatRooms')} />
            )}
          </>
        )}
      </div>
      {isRequestArtistSelectPopupOpen && (
        <RequestArtistSelectPopup onClick={handleNavigateArtistSelectButton} />
      )}
      {isPocapanNoticePopupOpen ? (
        <PocapanNoticePopup onClose={() => setIsPocapanNoticePopupOpen(false)} />
      ) : isBettiePromotionPopupOpen ? (
        <BettiePromotionPopup onClose={() => setIsBettiePromotionPopupOpen(false)} />
      ) : null}
      {isBettieSatisfactionPopupOpen && (
        <BettieSatisfactionBottomDrawer
          isOpen={isBettieSatisfactionPopupOpen}
          onClose={() => {
            setIsBettieSatisfactionPopupOpen(false);
            navigate(location.pathname, { replace: true, state: {} });
          }}
          thumbnailUrl={location.state?.thumbnailUrl}
          nickname={location.state?.nickname}
          chatRoomId={location.state?.chatRoomId}
        />
      )}
      <HomeBottomNav tabIndex={3} />
    </div>
  );
};

export default ChatMainPage;

const BettieAppDownloadBanner = () => {
  const handleDownloadClick = () => {
    window.open(getBettieDownloadUrl(), '_blank', 'noopener,noreferrer');
  };

  return (
    <button
      type="button"
      className="flex w-full items-center justify-between overflow-hidden bg-black py-[12px] pl-[32px] pr-[16px] text-left outline-none focus-visible:ring-2 focus-visible:ring-primary-light sm:py-[24px] sm:pl-[48px] sm:pr-[56px]"
      aria-label={String(t('ChatPage.bettieAppBanner.ariaLabel'))}
      onClick={handleDownloadClick}
    >
      <div className="flex min-w-0 flex-col justify-center gap-[6px] text-pure-white sm:gap-[14px]">
        <span className="block text-[17px] font-bold leading-[21px] sm:text-[21px] sm:leading-[25px]">
          {t('ChatPage.bettieAppBanner.eyebrow')}
        </span>
        <span className="block whitespace-nowrap text-[24px] font-extrabold leading-[28px] sm:text-[42px] sm:leading-[48px]">
          {t('ChatPage.bettieAppBanner.title')}
        </span>
        <span className="mt-[8px] inline-flex w-fit whitespace-nowrap rounded-full bg-pure-white px-[16px] py-[8px] text-[15px] font-bold leading-[18px] text-pure-black sm:mt-[10px] sm:px-[22px] sm:py-[10px] sm:text-[18px] sm:leading-[22px]">
          {t('ChatPage.bettieAppBanner.cta')}
        </span>
      </div>
      <img
        src="/assets/card_banner/appdown_object.png"
        alt=""
        aria-hidden="true"
        className="pointer-events-none h-[132px] w-[132px] flex-shrink-0 object-contain sm:h-[220px] sm:w-[220px]"
      />
    </button>
  );
};

const RequestArtistSelectPopup = ({ onClick }: { onClick: () => void }) => {
  const { colorMode } = useColorMode();
  return (
    <div
      className="min-h-screen fixed inset-0 z-[1000] flex flex-col items-center justify-center"
      style={{
        backgroundImage: `url(/assets/force_to_update/bg_ask_update_${colorMode}.png)`,
        backgroundRepeat: 'round'
      }}
    >
      <div className="flex flex-col items-center justify-center gap-[11px]">
        <img
          src="/assets/icons/collections/Milky Way.png"
          alt="milky way"
          className="w-[160px] h-[160px]"
        />
        <div className="flex flex-col px-[24px] py-[10px] gap-[14px]">
          <h1 className="text-[26px] font-bold text-text-main-lv1 leading-[31px] text-center">
            {t('requestArtistSelectPopup.title')}
          </h1>
          <p className="text-[15px] font-medium text-text-main-lv4 leading-[18px] text-center whitespace-pre-line">
            {t('requestArtistSelectPopup.description')}
          </p>
        </div>
      </div>
      <div className="mt-[26px]">
        <NavigateArtistSelectButton onClick={onClick} />
      </div>
    </div>
  );
};

const PocapanNoticePopup = ({ onClose }: { onClose: () => void }) => {
  const handleOpenNotice = () => {
    openNewTab('/bettie-notice/index.html');
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[1000] flex min-h-screen items-center justify-center bg-black/50"
      onClick={onClose}
    >
      <div
        className="relative inline-block outline-none"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className="relative block overflow-hidden rounded-[20px] text-left shadow-[0_18px_52px_rgba(0,0,0,0.28)]"
          onClick={handleOpenNotice}
        >
          <img
            src="/bettie-notice/assets/bettie-banner.png"
            alt={String(t('ChatPage.pocapanNotice.imageAlt'))}
            className="block w-[86vw] max-w-[560px] object-contain sm:max-w-[430px]"
          />
          <div className="absolute bottom-[24px] left-[24px] right-[24px] whitespace-nowrap rounded-[46px] bg-primary-light py-[18px] text-center text-[17px] font-bold leading-[20px] text-pure-white dark:bg-primary-dark">
            {t('ChatPage.pocapanNotice.button')}
          </div>
        </button>
        <button
          type="button"
          aria-label={String(t('close'))}
          className="absolute right-[18px] top-[18px] grid h-[34px] w-[34px] place-items-center rounded-full bg-black/80 text-[17px] font-bold leading-none text-white shadow-[0_8px_24px_rgba(0,0,0,0.24)] backdrop-blur"
          onClick={(event) => {
            event.stopPropagation();
            onClose();
          }}
        >
          X
        </button>
      </div>
    </div>
  );
};

const BettieEntryCard = () => {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      onClick={() => navigate('/bettie-setting?isFirst=true')}
      className="flex items-center gap-[12px] px-[20px] py-[12px]"
    >
      <div className="relative flex items-center justify-center">
        <PlusProfile width={46} height={46} className="[&>path]:fill-[#B2A3FF]" />
        <div className="z-10 absolute bottom-0 left-[35px] bg-[linear-gradient(90deg,#FF45A9_0%,#FF6A6A_100%)] rounded-full p-[2px] border border-pure-white dark:border-pure-black">
          <PeopleFill size={10} className="text-pure-white" />
        </div>
      </div>
      <div className="flex flex-col items-start gap-[4px]">
        <div className="flex items-center gap-[5px]">
          <p className="text-[15px] font-semibold text-text-main-lv2 leading-[18px]">
            {t('ChatPage.bettieEntryCard.title')}
          </p>
          <RoundBadge className="h-[17px] text-[10px] font-semibold px-[6px] py-[2.5px]">
            {t('ChatPage.category.bettie')}
          </RoundBadge>
        </div>
        <p className="text-[13px] text-text-main-lv3 leading-[16px]">
          {t('ChatPage.bettieEntryCard.startButton')}
        </p>
      </div>
    </button>
  );
};

const FreeChargeEntryCard = () => {
  const { rewardInfo, getRewardInfo, setUserId } = useAdPopcorn();
  const navigate = useNavigate();
  const user = useUserQuery();

  useEffect(() => {
    setUserId(user?.userId);
  }, [user, setUserId]);

  useEffect(() => {
    getRewardInfo();
  }, [getRewardInfo]);

  const rewardPoint = (rewardInfo as { point?: number } | undefined)?.point ?? 0;
  const rewardCountText = rewardPoint >= 1000 ? '1,000+' : String(rewardPoint);

  const navigateToFreeCharge = () => {
    navigate('/free-charge');
  };

  return (
    <button
      type="button"
      onClick={navigateToFreeCharge}
      className="flex items-center gap-[12px] px-[20px] py-[12px] w-full"
    >
      <div className="relative w-[46px] h-[46px] flex-shrink-0">
        <img
          src="/assets/icons/free_charge_circle.png"
          alt="free charge circle"
          className="w-full h-full"
          style={{
            animation: 'spin 6s linear infinite'
          }}
        />
        <img
          src="/assets/icons/free_charge_gift.png"
          alt="free charge gift"
          className="w-[30px] h-[30px] absolute top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%]"
        />
      </div>
      <div className="flex items-center justify-between w-full">
        <div className="flex flex-col items-start gap-[4px]">
          <p className="text-[15px] font-semibold text-text-main-lv2 leading-[18px]">
            {t('ChatPage.freeChargeEntryCard.title')}
          </p>
          <p className="text-[13px] text-text-main-lv3 leading-[16px] text-left">
            {t('ChatPage.freeChargeEntryCard.description', {
              count: rewardCountText as unknown as never
            })}
          </p>
        </div>
        <ArrowRightSmall size={18} className="text-text-main-lv4 flex-shrink-0" />
      </div>
    </button>
  );
};

const BettiePromotionPopup = ({ onClose }: { onClose: () => void }) => {
  const handleCloseForever = () => {
    saveData('isBettiePromotionPopupClosed', true);
    onClose();
  };

  return (
    <div
      className="fixed top-0 left-0 w-full min-h-screen bg-black/50 z-[1000] flex items-center justify-center"
      onClick={onClose}
    >
      <div className="bg-background-elevated rounded-[24px] mx-[32px] max-w-[311px] max-h-[90vh] overflow-y-auto">
        <div className="relative">
          <img
            src="/assets/bettie/bettie_promotion_popup.png"
            alt="bettie promotion popup"
            className="w-full h-full object-cover rounded-t-[24px]"
          />
          <p className="absolute bottom-[30px] left-[50%] translate-x-[-50%] text-[16px] font-medium text-[#3F4254] leading-[22px] w-full text-center whitespace-pre-line">
            <Trans
              i18nKey="ChatPage.bettiePopup.freeHeartPopup"
              components={{
                span: <span className="text-primary-light dark:text-primary-dark font-bold" />
              }}
            />
          </p>
        </div>
        <div className="py-[20px] px-[14px] flex flex-col gap-[16px] items-center">
          <Button size="large" className="w-full text-[17px] font-semibold" onClick={onClose}>
            {t('confirm')}
          </Button>
          <button
            type="button"
            className="text-[14px] text-text-main-lv4 leading-[17px] underline"
            style={{ textUnderlineOffset: '2px' }}
            onClick={handleCloseForever}
          >
            {t('ChatPage.bettiePopup.neverShow')}
          </button>
        </div>
      </div>
    </div>
  );
};
